export type ValidityResetScope = 'expired' | 'all' | 'active';
export type ValidityResetMode = 'renew_from_today' | 'align_from_start';

export type ValidityResetSample = {
  id: number;
  site_id: number;
  title: string;
  valid_start_date: string;
  valid_end_date: string;
  valid_days: number;
  sold_out: boolean;
  is_expired: boolean;
};

export type ValidityResetBySite = {
  site_id: number;
  matched: number;
  expired: number;
  valid_days: number;
  updated?: number;
};

export type ValidityResetResult = {
  ok: boolean;
  dry_run?: boolean;
  mode?: ValidityResetMode;
  scope?: ValidityResetScope;
  include_sold_out?: boolean;
  valid_days?: number | null;
  today?: string;
  matched?: number;
  updated?: number;
  skipped?: number;
  truncated?: boolean;
  limit?: number;
  samples?: ValidityResetSample[];
  by_site?: ValidityResetBySite[];
  site_id?: number;
  site_ids?: number[];
  detail?: string;
  err?: string;
};

export const VALIDITY_RESET_SCOPE_OPTIONS: Array<{
  value: ValidityResetScope;
  label: string;
  hint: string;
}> = [
  {
    value: 'expired',
    label: '仅已过期',
    hint: '结束日早于今天的信息（推荐，对应改配置后历史过期不自动延长的问题）',
  },
  {
    value: 'all',
    label: '全部信息',
    hint: '当前范围内所有信息（不含已售，除非勾选）',
  },
  {
    value: 'active',
    label: '仅展示中',
    hint: '仍在有效期内的信息，按新天数重算',
  },
];

export const VALIDITY_RESET_MODE_OPTIONS: Array<{
  value: ValidityResetMode;
  label: string;
  hint: string;
}> = [
  {
    value: 'renew_from_today',
    label: '自今日起重新计期',
    hint: '起始日改为今天，结束日 = 今天 + 天数 − 1。适合把已过期信息重新上架展示。',
  },
  {
    value: 'align_from_start',
    label: '保留起始日，按新天数对齐',
    hint: '不改发布起始日，只按当前配置天数重算结束日。起始日很早时仍可能过期。',
  },
];

export function validityResetScopeLabel(scope: ValidityResetScope): string {
  return VALIDITY_RESET_SCOPE_OPTIONS.find((x) => x.value === scope)?.label ?? scope;
}

export function validityResetModeLabel(mode: ValidityResetMode): string {
  return VALIDITY_RESET_MODE_OPTIONS.find((x) => x.value === mode)?.label ?? mode;
}

export function formatValidityResetSummary(r: ValidityResetResult, siteDaysHint?: number): string {
  const matched = r.matched ?? 0;
  const days =
    typeof r.valid_days === 'number' && r.valid_days > 0
      ? r.valid_days
      : siteDaysHint && siteDaysHint > 0
        ? siteDaysHint
        : null;
  const daysText = days ? `${days} 天` : '各站当前默认天数';
  if (r.dry_run) {
    return `预计影响 ${matched} 条，将按「${daysText}」执行${r.truncated ? '（已达单次上限，请分批）' : ''}`;
  }
  return `已更新 ${r.updated ?? 0} 条（匹配 ${matched}，跳过 ${r.skipped ?? 0}）`;
}
